import type { LocatorSearchItem } from '#types/brandify'

/**
 * Pinia store for managing BOPIS (Buy Online, Pick Up In Store) state.
 *
 * Handles store location selection, availability search, and form data for in-store pickup.
 * Scoped per country code to support multi-region deployments with independent pickup locations.
 *
 * **Persistence:** `selectedStore` and `form` (postal code + availability toggle) survive page
 * reloads via `pinia-plugin-persistedstate`. `stores` array is session-only to ensure fresh
 * availability data after navigation.
 *
 * **Multi-brand behavior:** Store locator UI and availability rules may vary by brand, but
 * state structure is consistent across all brands.
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code (defaults to current user's country)
 * @returns Pinia store instance scoped to the country
 * @example
 * // US store
 * const bopisUS = useBopisStore('US')
 * bopisUS.form.postalCode = '10001'
 * bopisUS.stores = await searchStores({ postalCode: '10001' })
 * bopisUS.selectedStore = bopisUS.stores[0]
 *
 * // Separate state for CA
 * const bopisCA = useBopisStore('CA')
 * bopisCA.form.postalCode = 'M5H 2N2'
 * @remarks
 * Persist config: `{ pick: ['selectedStore', 'form'] }` - only these fields are saved to
 * localStorage. The `stores` array is intentionally excluded to avoid stale availability data.
 */
export const useBopisStore = (countryCode = useCountryCode()) => defineStore(`bopis_${countryCode}`, () => {
  /**
   * Array of store locations returned from availability search.
   *
   * Populated by store locator API calls. Not persisted across sessions to ensure
   * availability data remains fresh.
   */
  const stores = ref<LocatorSearchItem[]>([])

  /**
   * Currently selected store location for pickup.
   *
   * Persisted to localStorage so users don't lose their selection when navigating away.
   */
  const selectedStore = ref<LocatorSearchItem>()

  /**
   * Reactive form state for store search.
   *
   * @property postalCode - User-entered postal/ZIP code for search
   * @property onlyStoresWithAvailability - If `true`, filters results to stores with current
   *   product in stock (product-specific search). If `false`, shows all nearby stores
   *   (general store locator).
   */
  const form = reactive({ postalCode: '', onlyStoresWithAvailability: false })

  /**
   * @returns Object containing reactive store state
   */
  return {
    form,
    stores,
    selectedStore,
  }
}, {
  persist: {
    pick: ['selectedStore', 'form']
  }
})()
